import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../auth';
import { useBilling, trialDaysRemaining } from '../billing';
import OnboardingWizard from './OnboardingWizard';
import { ENABLED_LANGUAGES, LANGUAGE_NAMES } from '../i18n/config';
import {
  IconDashboard,
  IconMail,
  IconFileText,
  IconUsers,
  IconZap,
  IconSettings,
  IconHistory,
  IconShield,
  IconLogOut,
  IconMenu,
  IconX,
  IconChevronLeft,
} from './Icons';

class PageErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', gap: '16px', padding: '2rem', textAlign: 'center' }}>
          <h2 style={{ fontSize: '1.25rem', fontWeight: 600 }}>Something went wrong</h2>
          <p style={{ color: 'var(--text-secondary)' }}>An unexpected error occurred on this page.</p>
          <button
            onClick={() => this.setState({ hasError: false })}
            style={{ padding: '8px 20px', borderRadius: '6px', background: 'var(--accent)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: '0.9rem' }}
          >
            Try again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}

interface NavItem {
  to: string;
  labelKey: string;
  icon: React.ReactNode;
}

const NAV_ITEMS: NavItem[] = [
  { to: '/app/dashboard',      labelKey: 'nav.dashboard',      icon: <IconDashboard /> },
  { to: '/app/subscriptions',  labelKey: 'nav.subscriptions',  icon: <IconMail /> },
  { to: '/app/invoices',       labelKey: 'nav.invoices',       icon: <IconFileText /> },
  { to: '/app/accounts',       labelKey: 'nav.accounts',       icon: <IconUsers /> },
  { to: '/app/auto-responses', labelKey: 'nav.autoResponses',  icon: <IconZap /> },
  { to: '/app/history',        labelKey: 'nav.history',        icon: <IconHistory /> },
  { to: '/app/settings',       labelKey: 'nav.settings',       icon: <IconSettings /> },
];

export default function AppShellLayout({ children }: { children: React.ReactNode }) {
  const { t, i18n } = useTranslation();
  const { loading: authLoading, user } = useAuth();
  const { billing, refresh, loading: billingLoading } = useBilling();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem('sidebar-collapsed') === 'true'
  );
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);

  const billingSuccess      = searchParams.get('billing') === 'success';
  const billingTrialStarted = searchParams.get('billing') === 'trial-started';
  const [paymentBannerDismissed, setPaymentBannerDismissed] = useState(false);

  useEffect(() => {
    fetch('/api/onboarding/status')
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data != null) setOnboardingCompleted(data.completed); })
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetch('/api/auth/is-admin')
      .then(res => res.ok ? res.json() : null)
      .then(data => { if (data != null) setIsAdmin(data.isAdmin === true); })
      .catch(() => {});
  }, []);

  // After a payment redirect, poll billing status until active (up to 10 s).
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    if (!billingSuccess || billing.status === 'active') {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return;
    }
    const timeout = setTimeout(() => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    }, 10000);
    pollRef.current = setInterval(() => refresh(), 2000);
    return () => {
      clearTimeout(timeout);
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, [billingSuccess, billing.status]); // eslint-disable-line react-hooks/exhaustive-deps

  // Once billing activates after a payment redirect, auto-dismiss the confirmation banner.
  useEffect(() => {
    if (!billingSuccess || billing.status !== 'active' || paymentBannerDismissed) return;
    const timer = setTimeout(() => setPaymentBannerDismissed(true), 4000);
    return () => clearTimeout(timer);
  }, [billingSuccess, billing.status, paymentBannerDismissed]);

  const closeSidebar = () => setSidebarOpen(false);
  const toggleCollapsed = () => setSidebarCollapsed(prev => {
    const next = !prev;
    localStorage.setItem('sidebar-collapsed', String(next));
    return next;
  });

  // --- Billing banner / gate ---
  const daysLeft       = trialDaysRemaining(billing.trialEndsAt);
  const isTrialExpired = billing.trialEndsAt !== null && new Date(billing.trialEndsAt) < new Date();
  const isTrialing     = billing.status === 'trialing' && !isTrialExpired && !billingSuccess;
  const isTrialGate    = billing.status === 'trialing' && isTrialExpired;
  const isPastDue      = billing.status === 'past_due';
  const isCanceled     = billing.status === 'canceled' && billing.plan !== null;
  const isNone         = billing.status === 'none' && !billingSuccess && !billingTrialStarted;

  const trialMessage = daysLeft === 0
    ? t('billing.trialEndsToday')
    : t('billing.trialDaysLeft', { count: daysLeft });

  // Hard gate: trial expired — user needs to add payment details
  if (isTrialGate && !isAdmin) {
    const upgradeUrl = `/pricing/checkout?plan=${billing.plan ?? 'starter'}&cycle=${billing.billingCycle ?? 'annual'}`;
    return (
      <div className="app-shell">
        <div className="billing-gate">
          <div className="billing-gate-card">
            <div className="billing-gate-icon">⏳</div>
            <h2>{t('billing.trialExpiredTitle')}</h2>
            <p>{t('billing.trialExpiredBody')}</p>
            <div className="billing-gate-actions">
              <Link to={upgradeUrl} className="btn-primary">{t('billing.upgradeNow')}</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Hard gate: canceled after having had a plan
  if (isCanceled && !isAdmin) {
    return (
      <div className="app-shell">
        <div className="billing-gate">
          <div className="billing-gate-card">
            <div className="billing-gate-icon">⏸</div>
            <h2>{t('billing.subscriptionEndedTitle')}</h2>
            <p>{t('billing.subscriptionEndedBody')}</p>
            <div className="billing-gate-actions">
              <Link to="/pricing" className="btn-primary">{t('billing.seePlans')}</Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="app-shell">
      {sidebarOpen && (
        <div className="sidebar-overlay" onClick={closeSidebar} aria-hidden="true" />
      )}

      <aside className={`sidebar${sidebarOpen ? ' sidebar--open' : ''}${sidebarCollapsed ? ' sidebar--collapsed' : ''}`}>
        <div className="sidebar-logo">
          <span className="sidebar-logo-mark">
            <svg width="16" height="12" viewBox="0 0 16 12" fill="none" aria-hidden="true">
              <rect x="0.5" y="0.5" width="15" height="11" rx="1.5" stroke="currentColor" strokeWidth="1.2" fill="none"/>
              <path d="M1 1.5L8 7L15 1.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/>
            </svg>
          </span>
          <span className="sidebar-logo-name">{t('app.name')}</span>
          <button className="sidebar-close" onClick={closeSidebar} aria-label={t('nav.closeMenu')}>
            <IconX size={16} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {NAV_ITEMS.map(({ to, labelKey, icon }) => {
            const active = pathname.startsWith(to);
            return (
              <Link
                key={to}
                to={to}
                className={`sidebar-link${active ? ' sidebar-link--active' : ''}`}
                onClick={closeSidebar}
              >
                <span className="sidebar-link-icon">{icon}</span>
                <span className="sidebar-link-label">{t(labelKey)}</span>
              </Link>
            );
          })}
          {isAdmin && (
            <a
              href="/admin"
              target="_blank"
              rel="noopener noreferrer"
              className="sidebar-link"
              onClick={closeSidebar}
            >
              <span className="sidebar-link-icon"><IconShield /></span>
              <span className="sidebar-link-label">{t('nav.admin')}</span>
            </a>
          )}
        </nav>

        <button
          className="sidebar-collapse-btn"
          onClick={toggleCollapsed}
          aria-label={sidebarCollapsed ? t('nav.expandSidebar') : t('nav.collapseSidebar')}
        >
          <IconChevronLeft size={16} className={sidebarCollapsed ? 'sidebar-collapse-icon--flipped' : undefined} />
        </button>

        <div className="sidebar-footer">
          {!authLoading && user && (
            <>
              <div className="sidebar-user">
                <div className="sidebar-avatar">
                  {user.userDetails?.charAt(0).toUpperCase()}
                </div>
                <span className="sidebar-user-email">{user.userDetails}</span>
              </div>
              <a href="/.auth/logout" className="sidebar-signout" title={t('nav.signOut')}>
                <IconLogOut size={16} />
              </a>
            </>
          )}
          {ENABLED_LANGUAGES.length > 1 && (
            <select
              className="sidebar-lang-select"
              value={i18n.resolvedLanguage ?? i18n.language}
              onChange={e => i18n.changeLanguage(e.target.value)}
              aria-label="Language"
            >
              {ENABLED_LANGUAGES.map(lang => (
                <option key={lang} value={lang}>{LANGUAGE_NAMES[lang] ?? lang}</option>
              ))}
            </select>
          )}
        </div>
      </aside>

      <div className="app-shell-main">
        {/* Trial countdown banner */}
        {!billingLoading && isTrialing && (
          <div className="trial-banner">
            <span>⏳ {trialMessage}</span>
            <Link
              to={`/pricing/checkout?plan=${billing.plan ?? 'starter'}&cycle=${billing.billingCycle ?? 'annual'}`}
              className="trial-banner-cta"
            >
              {t('billing.upgradeNow')}
            </Link>
          </div>
        )}

        {/* Past-due warning banner */}
        {!billingLoading && isPastDue && (
          <div className="trial-banner trial-banner--warn">
            <span>⚠️ {t('billing.paymentFailed')}</span>
            <button
              className="trial-banner-cta"
              onClick={() => {
                fetch('/api/billing/portal', { method: 'POST' })
                  .then(r => r.json())
                  .then(({ url }) => { window.location.href = url; })
                  .catch(() => {});
              }}
            >
              {t('billing.updatePayment')}
            </button>
          </div>
        )}

        {/* No subscription yet — soft prompt */}
        {!billingLoading && isNone && (
          <div className="trial-banner trial-banner--info">
            <span>{t('billing.startTrial')}</span>
            <Link to="/pricing/checkout" className="trial-banner-cta">{t('billing.startFreeTrialCta')}</Link>
          </div>
        )}

        {/* Trial started confirmation */}
        {billingTrialStarted && (
          <div className="trial-banner trial-banner--success">
            <span>🎉 {t('billing.trialStarted')}</span>
          </div>
        )}

        {/* Payment confirmation */}
        {billingSuccess && !paymentBannerDismissed && (
          <div className="trial-banner trial-banner--success">
            <span>
              {billing.status === 'active'
                ? `🎉 ${t('billing.paymentConfirmed')}`
                : `⏳ ${t('billing.paymentActivating')}`}
            </span>
          </div>
        )}

        <header className="app-topbar">
          <button
            className="topbar-menu-btn"
            onClick={() => setSidebarOpen(true)}
            aria-label={t('nav.openMenu')}
          >
            <IconMenu size={20} />
          </button>
          <span className="topbar-logo-name">{t('app.name')}</span>
        </header>

        <main className="app-shell-content"><PageErrorBoundary>{children}</PageErrorBoundary></main>
      </div>

      {onboardingCompleted === false && !isAdmin && pathname !== '/app/connect' && (
        <OnboardingWizard onDismiss={() => setOnboardingCompleted(true)} />
      )}
    </div>
  );
}
